import { ref, computed } from 'vue';
import { Capacitor } from '@capacitor/core';
import { CapacitorReadNativeSetting } from 'capacitor-read-native-setting';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { ActionPerformed, PushNotificationSchema, PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';
import { QueryClient, QueryObserver, useQueryClient } from '@tanstack/vue-query';
import router from '@/router';

import NativeAppConfig from '@/config/native-app.config.json';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import { useStyles } from '@/shared/composables/useStyles';
import type { TenantConfig, TenantStyles } from '@/shared/interfaces/tenant';
import useTenant from '@/shared/composables/useTenant';
import useQueryCacheHelper from '@/shared/composables/useQueryCacheHelper';
import useUtils from '@/shared/composables/useUtils';
import { getTokenFromMagicLinkUrl } from '@/shared/composables/useUrlHelpers';
import { useAuthStore } from '@/stores/auth/authStore';
import useStorageService from './useStorageService';

export const useApp = () => {
	const isNative = computed(() => Capacitor.isNativePlatform());
	const assetStore = useAssetsStore();
	const { setStyles } = useStyles();
	const { getStyles, getConfig } = useTenant();
	const queryClient = useQueryClient();
	const { removeCaches } = useQueryCacheHelper();
	const { activateMagicLink, openURL } = useUtils();
	const { get, set } = useStorageService();

	const loadApp = ref(false);
	const tenantConfigLoaded = ref(false);
	const tenantStylesLoaded = ref(false);
	const ENVIRONMENT_DEBUG = process.env.VUE_APP_ENVIRONMENT_DEBUG;
	const tenantStylesObserver = new QueryObserver(queryClient, { queryKey: ['tenantStyles'], queryFn: () => getStyles() });
	const tenantConfigObserver = new QueryObserver(queryClient, { queryKey: ['tenantConfig'], queryFn: () => getConfig() });

	// Load tenant details
	const loadTenantDetails = async () => {
		Preferences.get({ key: 'tenantDetails' }).then(async (tenantDetailsLocalStorage) => {
			// Get tenant details from local storage
			// If it's not there, read from native settings
			if (!tenantDetailsLocalStorage.value) {
				// Read from native settings
				let apiUrl = NativeAppConfig.API_URL;
				let centralUrl = NativeAppConfig.CENTRAL_URL;
				let tenantName = NativeAppConfig.TENANT_NAME;
				let logoUrl = NativeAppConfig.LOGO_URL;
				let bgImageUrl = NativeAppConfig.BACKGROUND_URL;
				let primaryColor = NativeAppConfig.PRIMARY_COLOR;
				let secondaryColor = NativeAppConfig.SECONDARY_COLOR;
				let tertiaryColor = NativeAppConfig.TERTIARY_COLOR;
				if (Capacitor.isPluginAvailable('CapacitorReadNativeSetting')) {
					const apiUrlFromNative = await CapacitorReadNativeSetting.read({ key: 'api_url' });
					const centralUrlFromNative = await CapacitorReadNativeSetting.read({ key: 'central_url' });
					const tenantNameFromNative = await CapacitorReadNativeSetting.read({ key: 'tenant_name' });
					const logoUrlFromNative = await CapacitorReadNativeSetting.read({ key: 'logo_url' });
					const bgImageUrlFromNative = await CapacitorReadNativeSetting.read({ key: 'bg_image_url' });
					const primaryColorFromNative = await CapacitorReadNativeSetting.read({ key: 'primary_color' });
					const secondaryColorFromNative = await CapacitorReadNativeSetting.read({ key: 'secondary_color' });
					const tertiaryColorFromNative = await CapacitorReadNativeSetting.read({ key: 'tertiary_color' });

					apiUrl = apiUrlFromNative.value;
					centralUrl = centralUrlFromNative.value;
					tenantName = tenantNameFromNative.value;
					logoUrl = logoUrlFromNative.value;
					bgImageUrl = bgImageUrlFromNative.value;
					primaryColor = primaryColorFromNative.value;
					secondaryColor = secondaryColorFromNative.value;
					tertiaryColor = tertiaryColorFromNative.value;
				}

				const tenantDetails = {
					api_url: apiUrl,
					central_url: centralUrl,
					tenant_name: tenantName,
					logo_url: logoUrl,
					bg_image_url: bgImageUrl,
					primary_color: primaryColor,
					secondary_color: secondaryColor,
					tertiary_color: tertiaryColor,
				};

				console.log('Reading tenant details from native settings', tenantDetails);

				// Set initial details.
				Preferences.set({
					key: 'tenantDetails',
					value: JSON.stringify(tenantDetails),
				});

				// Set the values of the window object
				setStyles(tenantDetails);

				// Dispatch an event to notify the app that the apiUrl has changed
				document.dispatchEvent(new Event('apiUrlChanged'));

				// Set loadApp to true
				loadApp.value = true;
			} else {
				// Fetch tenant details from local storage
				const tenantDetails = JSON.parse(tenantDetailsLocalStorage.value);
				if (tenantDetails) {
					console.log('Tenant details fetched from local storage.', tenantDetails);
					setStyles(tenantDetails);
					document.dispatchEvent(new Event('apiUrlChanged'));
				}

				// Set loadApp to true
				loadApp.value = true;
			}
		});
	};

	const saveInCache = async (key: string, tenantData: any) => {
		let keys = await get('cachedDataKeys');
		if (!keys) {
			keys = [key];
		} else {
			if (!keys.includes(key)) {
				keys.push(key);
			}
		}
		await set('cachedDataKeys', keys);

		// Get data from server if it doesn't exists on cache
		// Cache will be keep updated every time a query is requested
		// to backend.
		// So if cache is present, then use it as it will be updated
		const data = await get(key);
		if (!data) {
			await set(key, tenantData);
		}
	};

	const handleSubscribeToConfig = async () => {
		// Subscribe to updates on config
		const unsubscribe = tenantConfigObserver.subscribe(async (result) => {
			const tenantConfigData = ref<TenantConfig>(result.data as TenantConfig);
			if (tenantConfigData.value && Object.keys(tenantConfigData.value).length > 0) {
				await saveInCache('tenantConfig', tenantConfigData.value);
				tenantConfigLoaded.value = true;
			}
		});
	};

	const updateTenantDetails = async (tenantStylesData: TenantStyles) => {
		Preferences.get({ key: 'tenantDetails' }).then((tenantDetailsLocalStorage) => {
			if (tenantDetailsLocalStorage && tenantDetailsLocalStorage.value) {
				const tenantDetails = JSON.parse(tenantDetailsLocalStorage.value);

				if (tenantDetails) {
					tenantDetails.primary_color = tenantStylesData.primary_color ?? tenantDetails.primary_color;
					tenantDetails.secondary_color = tenantStylesData.secondary_color ?? tenantDetails.secondary_color;
					tenantDetails.tertiary_color = tenantStylesData.tertiary_color ?? tenantDetails.tertiary_color;
					tenantDetails.logo_url = tenantStylesData.logo_url ?? tenantDetails.logo_url;
					tenantDetails.bg_image_url = tenantStylesData.bg_image_url ?? tenantDetails.bg_image_url;

					Preferences.set({
						key: 'tenantDetails',
						value: JSON.stringify(tenantDetails),
					});

					setStyles(tenantDetails);
				}
			} else {
				console.log('Tenant Styles observer: Failed to fetch tenant details from preferences.');
			}
		});
	};

	const handleSubscribeToStyles = () => {
		// Subscribe to updates on styles
		const unsubscribe = tenantStylesObserver.subscribe(async (result) => {
			const tenantStylesData = ref<TenantStyles>(result.data as TenantStyles);

			if (tenantStylesData.value && Object.keys(tenantStylesData.value).length > 0) {
				await saveInCache('tenantStyles', tenantStylesData.value);
				tenantStylesLoaded.value = true;

				if (Capacitor.isNativePlatform()) {
					updateTenantDetails(tenantStylesData.value);
				}
			}
		});
	};

	// Handle not native platform
	const handleNotNative = () => {
		// Set loadApp to true
		loadApp.value = true;

		// we are not in debug mode and on web -- let's inject the necessary styles into the pinia store for vue to pick up
		// in normal circumstances this information is simply in index.html
		// let's not do more than this -- we want to make sure the backend can also override styles when they are updated.
		if (window.logoUrl && !window.logoUrl?.includes('REPLACE') && window.backgroundUrl && !window.backgroundUrl?.includes('REPLACE')) {
			assetStore.updateAssetsValues({
				logoUrl: window.logoUrl,
				backgroundUrl: window.backgroundUrl,
			});
		}
	};

	const handleDebugMode = () => {
		// Debug
		if (ENVIRONMENT_DEBUG === 'true') {
			window.apiUrl = process.env.VUE_APP_API_URL ?? NativeAppConfig.API_URL;
			window.tenantName = process.env.VUE_APP_TENANT_NAME ?? NativeAppConfig.TENANT_NAME;
			window.logoUrl = process.env.VUE_APP_LOGO_URL ?? NativeAppConfig.LOGO_URL;
			window.backgroundUrl = process.env.VUE_APP_BACKGROUND_URL ?? NativeAppConfig.BACKGROUND_URL;
			window.primary_color = process.env.VUE_APP_PRIMARY_COLOR ?? NativeAppConfig.PRIMARY_COLOR;
			window.secondary_color = process.env.VUE_APP_SECONDARY_COLOR ?? NativeAppConfig.SECONDARY_COLOR;
			window.tertiary_color = process.env.VUE_APP_TERTIARY_COLOR ?? NativeAppConfig.TERTIARY_COLOR;
			window.centralUrl = process.env.VUE_APP_CENTRAL_URL ?? NativeAppConfig.CENTRAL_URL;

			// Updating store
			assetStore.updateAssetsValues({
				logoUrl: window.logoUrl,
				backgroundUrl: window.backgroundUrl,
			});

			document.dispatchEvent(new Event('apiUrlChanged'));
		}
	};

	const handleRedirection = (url: string, path: string) => {
		let query;
		if (url.includes('#')) {
			const [, queryString] = url.split('#')[1].split('?');
			query = Object.fromEntries(new URLSearchParams(queryString));
		} else {
			const [, queryString] = url.split('?');
			query = Object.fromEntries(new URLSearchParams(queryString));
		}

		router.push({
			path,
			query: {
				...query,
			},
		});
	};

	const listenForNotifications = () => {
		if (Capacitor.isNativePlatform()) {
			PushNotifications.addListener('registrationError', (error: any) => {
				console.error('Error on registration: ', error);
			});

			PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
				console.log('notification received!', notification);
				if (Capacitor.isPluginAvailable('Badge')) {
					try {
						await Badge.set({ count: 1 });
					} catch (e) {
						console.warn('Badge.set() failed, native plugin may not be updated yet', e);
					}
				}
			});

			PushNotifications.addListener('pushNotificationActionPerformed', async (action: ActionPerformed) => {
				console.log('pushNotificationActionPerformed', action);
				const {
					notification: { data: notificationData },
				} = action;

				if (notificationData?.url) {
					const pathname = new URL(notificationData?.url).pathname;
					const hash = new URL(notificationData?.url).hash;
					const path = hash != '' ? `${hash.substring(1)}` : pathname;

					document.addEventListener(
						'deviceready',
						() => {
							handleRedirection(notificationData.url, path);
						},
						{
							once: true,
						},
					);
				}
			});
		}
	};

	const initAppLinksListener = () => {
		// Deeplink handler
		App.addListener('appUrlOpen', async function (event: URLOpenListenerEvent) {
			// Example: https://testing.anykrowd.review/events/68
			// pathname = /events/68
			const pathname = new URL(event.url).pathname;
			const search = new URL(event.url).search;
			const hash = new URL(event.url).hash;

			if (pathname || hash) {
				// Push to the route
				const path = hash != '' ? `${hash.substring(1)}` : pathname;
				if (path.includes('magiclink')) {
					const magicLinkToken = getTokenFromMagicLinkUrl(event.url);
					activateMagicLink(magicLinkToken!).then(async (res) => {
						const token = res?.token;
						let redirectUrl = res?.redirect_url;
						// decode url to navigate
						try {
							redirectUrl = decodeURIComponent(redirectUrl);
						} catch (e) {
							console.log(e);
						}
						if (token) {
							const authStore = useAuthStore();
							await authStore.saveToken({ token });
						}
						if (redirectUrl) {
							const hashIndex = redirectUrl.indexOf('#');
							if (hashIndex > -1) {
								const path = redirectUrl.substring(hashIndex + 2);
								if (path) {
									router.push({ path });
								} else {
									await openURL(redirectUrl);
								}
							} else {
								await openURL(redirectUrl);
							}
							return;
						}
						await router.push({ path: '/dashboard' });
					});
				} else {
					handleRedirection(event.url, path);
				}
			}
		});
	};

	// Prefetch tenant config and styles
	const prefetch = async (queryClient: QueryClient, queryKey: any, queryFn: any) => {
		await queryClient.prefetchQuery({
			queryKey,
			queryFn,
			cacheTime: 1000 * 60 * 60 * 24 * 365,
		});
	};

	// Listener for apiUrlChanged event
	document.addEventListener('apiUrlChanged', async () => {
		await prefetch(queryClient, ['tenantConfig'], () => getConfig());
		await prefetch(queryClient, ['tenantStyles'], () => getStyles());
	});

	// Handle logout event triggered by a deleteToken
	window.document.addEventListener(
		'logout',
		async (e) => {
			// Remove caches
			removeCaches();

			// Reload page
			location.href = '/';
		},
		false,
	);

	const hideOfflineMessage = () => {
		document.getElementsByClassName('network-status')[0]?.classList.remove('visible');
		document.getElementsByClassName('network-status')[0]?.classList.add('hidden');
	};

	const showOfflineMessage = () => {
		document.getElementsByClassName('network-status')[0]?.classList.remove('hidden');
		document.getElementsByClassName('network-status')[0]?.classList.add('visible');
	};

	// Show offline message when offline
	window.addEventListener('offline', (e) => {
		showOfflineMessage();
	});

	// Hide offline message when online
	window.addEventListener('online', (e) => {
		hideOfflineMessage();
	});

	return {
		handleDebugMode,
		handleSubscribeToConfig,
		handleSubscribeToStyles,
		handleNotNative,
		loadTenantDetails,
		hideOfflineMessage,
		initAppLinksListener,
		isNative,
		listenForNotifications,
		loadApp,
		prefetch,
		tenantConfigLoaded,
		tenantStylesLoaded,
	};
};
